import { environmentManager } from '@tanstack/react-query';
import { getStatistics } from '@/actions/statistics';
import type { StatisticsSeries } from '@/types/series';
import type { StatisticsData } from '@/types/statisticsApi';
import type { Compare, StatisticsResource } from './types';

/**
 * Single entry point for statistics reads, usable from anywhere:
 * - On the server (RSC, prefetchQuery, StatisticsQueryServer) it calls getStatistics directly — a plain function call.
 * - In the browser, GETs call Django's /pro-api directly (same origin) instead of the server action, because Next
 *   dispatches server actions one at a time; direct requests run in parallel.
 * - POSTs (long id lists, e.g. the projects table) stay on the server action, which handles CSRF. They're single
 *   requests, so the one-at-a-time queue doesn't cost anything there.
 */
const fetchStatistics = async <Series extends StatisticsSeries, C extends Compare>(
  resource: StatisticsResource,
  params: string,
  method: 'GET' | 'POST' = 'GET',
): Promise<StatisticsData<Series, C>> => {
  if (environmentManager.isServer() || method === 'POST') {
    return getStatistics<Series, C>(resource, params, method);
  }

  const res = await fetch(`/pro-api/statistics/v2/${resource}?${params}`, {
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`API Error: ${res.status}`, { cause: res.statusText });
  }

  return (await res.json()) as StatisticsData<Series, C>;
};

export { fetchStatistics };
